import { Grid, Button, Dialog, DialogTitle, DialogContent, Tooltip, Box } from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { useState } from "react";
import { useUserSlice } from "src/store/features/user.slice";
import { useOnboarding } from "src/store/features/onboarding.slice";
import { trackEvent } from "src/Amplitude";

export default function ReKycFileUploadBanner() {
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");

  const {
    reKycDetails: { edd, recordType },
  } = useUserSlice();
  const { reKycApplicationStep } = useOnboarding();

  const handleClickOpen = (label: string) => {
    trackEvent("Button_Click", { button: label }, "Re-KYC");
    setSelectedLabel(label);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedLabel("");
  };
  const reKycFileUploadNote = `Note: Please ensure your document is in PDF/JPEG/JPG/PNG format and includes your full name and residential address clearly. 
  Maximum file size is 50MB`;

  const LabelForGeneralClients = () => {
    return (
      <ul>
        <li>Current and valid driving licence</li>
        <li>A service/utility bill dated in the last 90 days</li>
        <li>A current lease agreement</li>
        <li>A bank statement dated in the last 90 days</li>
        <li>An income statement, e.g., payslip</li>
      </ul>
    );
  };

  // edd = Enhanced Due Diligence
  const LabelForEddClients = () => {
    return (
      <ul>
        <li style={{ fontWeight: 600 }}>Income:</li>
        <ul style={{ marginBottom: "10px", marginTop: "10px" }}>
          <li>
            Bank statements for at least the last 3 months showing the savings accumulated from your
            regular income
          </li>
        </ul>
        <li style={{ fontWeight: 600 }}>Investments</li>
        <ul style={{ marginBottom: "10px", marginTop: "10px" }}>
          <li>
            Documents relating to your current investment holdings, or the sale of your investments
            in the last 12 months
          </li>
          <li>
            Transaction or account statements reflecting the transactions and balances for at least
            the last 3 months e.g., private wallet, exchange platform, fund manager, with the
            platform/provider name visible
          </li>
          <li>The wallet addresses from which crypto funds are coming from</li>
        </ul>
        <li style={{ fontWeight: 600 }}>Savings</li>
        <ul style={{ marginBottom: "10px", marginTop: "10px" }}>
          <li>Bank statements for at least the last 3 months showing your accumulated savings</li>
        </ul>
        <li style={{ fontWeight: 600 }}>Property Income</li>
        <ul style={{ marginBottom: "10px", marginTop: "10px" }}>
          <li>
            Bank statements for at least the last 3 months showing the savings accumulated from your
            property income
          </li>
        </ul>
        <li style={{ fontWeight: 600 }}>Private Pension</li>
        <ul style={{ marginBottom: "10px", marginTop: "10px" }}>
          <li>Documents detailing pension payouts from the last 3 months</li>
        </ul>
        <li style={{ fontWeight: 600 }}>Inheritance</li>
        <ul style={{ marginBottom: "10px", marginTop: "10px" }}>
          <li>
            Documents relating to the money received from your inheritance, such as the last will or
            trust deed
          </li>
        </ul>
        <li style={{ fontWeight: 600 }}>Others</li>
        <ul style={{ marginBottom: "10px", marginTop: "10px" }}>
          <li>Other documents that evidence your source of funds and source of wealth</li>
        </ul>
      </ul>
    );
  };

  const ReKycEnitityDocumentHelper = () => {
    return (
      <Box
        sx={{
          borderRadius: "20px",
          padding: "20px",
          background: "#FEFCF8",
          border: "1px solid black",
          pb: "0px",
          mb: "10px",
        }}>
        <p style={{ padding: "0px", margin: "0px" }}>Document Submission Guidelines:</p>
        <ul style={{ lineHeight: "30px" }}>
          <li>
            Please provide a document disclosing your entity’s current beneficial owners. A
            beneficial owner is an individual who ultimately owns OR controls your entity such as a
            company, trust or partnership.
          </li>
          <ul>
            <li>
              Owns’ means owning 25% or more of the entity. This can be directly (such as through
              shareholdings) or indirectly (such as through another entity’s ownership).
            </li>
            <li>
              Controls’ means having the power to make decisions about the entity’s finances and
              operations, such as a manager, director, or trustee.
            </li>
          </ul>
        </ul>
        <p style={{ padding: "0px", margin: "0px" }}></p>
        <ul></ul>
        <p style={{ color: "#fc4c4c", fontSize: "16px" }}>
          Note: If you do not have this on hand, we encourage you to document the beneficial owners
          and upload it here for our review. Please include their email addresses and phone numbers.
          Maximum file size is 50MB.
        </p>
      </Box>
    );
  };

  const renderSelectedLabel = () => {
    if (selectedLabel === "proofOfResidentialAddress") {
      return <LabelForGeneralClients />;
    } else if (selectedLabel === "proofOfWealth") {
      return <LabelForEddClients />;
    } else {
      return null;
    }
  };

  return (
    <Grid item xs={12} pt={"20px"}>
      {reKycApplicationStep === "aboutYou" && (
        <Box
          sx={{
            borderRadius: "20px",
            padding: "20px",
            background: "#FEFCF8",
            border: "1px solid black",
            marginBottom: "10px",
          }}>
          <p style={{ marginBottom: "10px", marginTop: "0px" }}>Please submit the following:</p>
          <ul style={{ marginTop: "0px" }}>
            <li>
              A proof of residential address document
              <Tooltip
                componentsProps={{
                  tooltip: {
                    sx: {
                      width: "140px",
                      height: "30px",
                      fontSize: "14px",
                      textAlign: "center",
                    },
                  },
                }}
                arrow
                title={"Click for Examples"}
                placement="top">
                <Button
                  disableRipple
                  sx={{ color: "#fc4c4c", pl: "0px" }}
                  startIcon={<HelpOutlineRoundedIcon />}
                  onClick={() => handleClickOpen("proofOfResidentialAddress")}
                />
              </Tooltip>
            </li>
            {recordType === "Individual" && edd && (
              <li>
                A proof of source of funds and source of wealth (1 for each){" "}
                <Tooltip
                  arrow
                  title={"Click for Examples"}
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        width: "140px",
                        height: "30px",
                        fontSize: "14px",
                        textAlign: "center",
                      },
                    },
                  }}>
                  <Button
                    disableRipple
                    sx={{ color: "#fc4c4c", pl: "0px" }}
                    startIcon={<HelpOutlineRoundedIcon />}
                    onClick={() => handleClickOpen("proofOfWealth")}
                  />
                </Tooltip>
              </li>
            )}
          </ul>
          <Dialog
            PaperProps={{ sx: { borderRadius: "20px", background: "#FEFCF8" } }}
            open={open}
            onClose={handleClose}>
            <DialogTitle sx={{ pb: "0px" }}>{"Accepted Documents"}</DialogTitle>
            <DialogContent sx={{ pr: "40px" }}>{renderSelectedLabel()}</DialogContent>
          </Dialog>
          {reKycApplicationStep === "aboutYou" && (
            <p style={{ color: "#fc4c4c", margin: "0px", fontSize: "16px" }}>
              {reKycFileUploadNote}
            </p>
          )}
        </Box>
      )}

      {reKycApplicationStep === "aboutYourEntity" && <ReKycEnitityDocumentHelper />}
    </Grid>
  );
}
